import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Trash2, LogOut, Image as ImageIcon, AlertCircle, Download, Inbox } from "lucide-react";
import { toast } from "sonner";
import {
 listProjectPhotos,
 verifyAdmin,
 uploadProjectPhoto,
 deleteProjectPhoto,
 listSubmissions,
 downloadSubmissionsCsv,
 resolvePhotoUrl,
} from "@/lib/api";

const TOKEN_KEY = "hh_admin_token";

export default function Admin() {
 const navigate = useNavigate();
 const [token, setToken] = useState("");
 const [authed, setAuthed] = useState(false);
 const [checking, setChecking] = useState(true);
 const [photos, setPhotos] = useState([]);
 const [loading, setLoading] = useState(false);
 const [uploading, setUploading] = useState(false);
 const [pendingDelete, setPendingDelete] = useState(null);
 const [file, setFile] = useState(null);
 const [caption, setCaption] = useState("");
 const [submissions, setSubmissions] = useState([]);
 const [subsLoading, setSubsLoading] = useState(false);

 // Try existing token from localStorage
 useEffect(() => {
 const saved = localStorage.getItem(TOKEN_KEY);
 if (!saved) {
 setChecking(false);
 return;
 }
 verifyAdmin(saved)
 .then(() => {
 setToken(saved);
 setAuthed(true);
 })
 .catch(() => localStorage.removeItem(TOKEN_KEY))
 .finally(() => setChecking(false));
 }, []);

 const loadPhotos = async () => {
 setLoading(true);
 try {
 const data = await listProjectPhotos();
 setPhotos(data);
 } catch (e) {
 toast.error("Could not load photos.");
 } finally {
 setLoading(false);
 }
 };

 const loadSubmissions = async (tok) => {
 setSubsLoading(true);
 try {
 const data = await listSubmissions(tok);
 setSubmissions(data);
 } catch (e) {
 toast.error("Could not load inquiries.");
 } finally {
 setSubsLoading(false);
 }
 };

 useEffect(() => {
 if (authed && token) {
 loadPhotos();
 loadSubmissions(token);
 }
 }, [authed, token]);

 const handleLogin = async (e) => {
 e.preventDefault();
 if (!token.trim()) return;
 try {
 await verifyAdmin(token.trim());
 localStorage.setItem(TOKEN_KEY, token.trim());
 setAuthed(true);
 toast.success("Signed in.");
 } catch {
 toast.error("Invalid admin token.");
 }
 };

 const handleLogout = () => {
 localStorage.removeItem(TOKEN_KEY);
 setToken("");
 setAuthed(false);
 setPhotos([]);
 };

 const handleUpload = async (e) => {
 e.preventDefault();
 if (!file) {
 toast.error("Choose an image first.");
 return;
 }
 setUploading(true);
 try {
 await uploadProjectPhoto({ token, file, caption: caption.trim() });
 toast.success("Photo added to the gallery.");
 setFile(null);
 setCaption("");
 const input = document.querySelector('input[type="file"]');
 if (input) input.value = "";
 await loadPhotos();
 } catch (err) {
 const msg = err?.response?.data?.detail || "Upload failed.";
 toast.error(msg);
 } finally {
 setUploading(false);
 }
 };

 const handleDelete = async (photo) => {
 try {
 await deleteProjectPhoto({ token, id: photo.id });
 toast.success("Photo removed.");
 setPendingDelete(null);
 setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
 } catch {
 toast.error("Could not delete that photo.");
 }
 };

 if (checking) {
 return (
 <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-loading">
 <div className="overline">Verifying…</div>
 </div>
 );
 }

 if (!authed) {
 return (
 <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="admin-login">
 <form
 onSubmit={handleLogin}
 className="w-full max-w-md border p-8 md:p-10"
 style={{ background: "var(--hh-bg-2)", borderColor: "var(--hh-border)" }}
 >
 <div className="overline mb-4">Admin Access</div>
 <h1 className="font-display text-3xl md:text-4xl mb-3">Sign in</h1>
 <p className="text-sm mb-8" style={{ color: "var(--hh-text-dim)" }}>
 Enter the admin token to manage the project gallery.
 </p>
 <label className="hh-label" htmlFor="admin-token">Admin Token</label>
 <input
 id="admin-token"
 data-testid="admin-token-input"
 type="password"
 value={token}
 onChange={(e) => setToken(e.target.value)}
 className="hh-input"
 placeholder="••••••••••••••••"
 autoFocus
 />
 <button
 data-testid="admin-sign-in"
 type="submit"
 className="btn-gold w-full mt-8"
 >
 Sign In
 </button>
 </form>
 </div>
 );
 }

 return (
 <div data-testid="admin-page" className="px-6 md:px-10 py-20 md:py-28">
 <div className="max-w-[1480px] mx-auto">
 {/* Header */}
 <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
 <div>
 <div className="overline mb-4">Admin</div>
 <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
 Manage Project Gallery
 </h1>
 <p className="mt-4 max-w-xl text-sm" style={{ color: "var(--hh-text-dim)" }}>
 Upload new photos or remove existing ones. Changes appear on the public Projects page immediately.
 </p>
 </div>
 <div className="flex gap-3">
 <button
 data-testid="admin-view-public"
 onClick={() => navigate("/projects")}
 className="btn-ghost"
 >
 View Public Page
 </button>
 <button
 data-testid="admin-sign-out"
 onClick={handleLogout}
 className="btn-ghost"
 >
 <LogOut size={14} /> Sign Out
 </button>
 </div>
 </div>

 {/* Inquiries */}
 <section data-testid="admin-submissions" className="mb-14">
 <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
 <div className="overline">Contact Inquiries · {submissions.length}</div>
 <button
 data-testid="admin-submissions-csv"
 onClick={() => downloadSubmissionsCsv(token).catch(() => toast.error("Could not download CSV."))}
 className="btn-ghost"
 disabled={subsLoading}
 >
 <Download size={14} /> Download CSV
 </button>
 </div>
 {subsLoading ? (
 <div className="py-12 text-center overline" data-testid="admin-submissions-loading">Loading…</div>
 ) : submissions.length === 0 ? (
 <div className="py-12 text-center border" style={{ borderColor: "var(--hh-border)" }} data-testid="admin-submissions-empty">
 <Inbox size={28} strokeWidth={1} style={{ color: "var(--hh-gold)" }} className="mx-auto mb-3" />
 <div className="overline">No inquiries yet.</div>
 </div>
 ) : (
 <div className="space-y-3">
 {submissions.map((s) => (
 <div
 key={s.id}
 data-testid={`admin-submission-${s.id}`}
 className="border p-5 md:p-6"
 style={{ borderColor: "var(--hh-border)", background: "var(--hh-bg-2)" }}
 >
 <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
 <span className="font-display text-xl">{s.name}</span>
 <span className="text-[10px] tracking-[0.32em] uppercase" style={{ color: "var(--hh-gold)" }}>
 {s.project_type}
 </span>
 <span className="text-xs" style={{ color: "var(--hh-text-dim)" }}>
 {new Date(s.created_at).toLocaleString()}
 </span>
 <span
 data-testid={`admin-submission-email-flag-${s.id}`}
 className="text-[10px] tracking-[0.32em] uppercase"
 style={{ color: s.email_sent ? "var(--hh-gold)" : "var(--hh-text-dim)" }}
 >
 {s.email_sent ? "Email sent" : "Email not sent"}
 </span>
 </div>
 <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-2">
 <a href={`mailto:${s.email}`} className="underline underline-offset-4" style={{ color: "var(--hh-gold)" }}>
 {s.email}
 </a>
 <a href={`tel:${s.phone}`} className="underline underline-offset-4" style={{ color: "var(--hh-gold)" }}>
 {s.phone}
 </a>
 </div>
 <p className="text-sm leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>{s.message}</p>
 </div>
 ))}
 </div>
 )}
 </section>

 {/* Upload form */}
 <form
 data-testid="admin-upload-form"
 onSubmit={handleUpload}
 className="border p-6 md:p-8 mb-14"
 style={{ background: "var(--hh-bg-2)", borderColor: "var(--hh-border)" }}
 >
 <div className="overline mb-5">Add a Photo</div>
 <div className="grid md:grid-cols-12 gap-5 items-end">
 <div className="md:col-span-5">
 <label className="hh-label">Image File (JPG, PNG, WEBP, max 10 MB)</label>
 <input
 data-testid="admin-file-input"
 type="file"
 accept="image/jpeg,image/png,image/webp"
 onChange={(e) => setFile(e.target.files?.[0] ?? null)}
 className="hh-input"
 style={{ padding: "10px 12px" }}
 />
 </div>
 <div className="md:col-span-5">
 <label className="hh-label">Caption (optional)</label>
 <input
 data-testid="admin-caption-input"
 type="text"
 value={caption}
 onChange={(e) => setCaption(e.target.value)}
 placeholder="e.g. Modern farmhouse, Cherokee County"
 maxLength={140}
 className="hh-input"
 />
 </div>
 <div className="md:col-span-2">
 <button
 data-testid="admin-upload-button"
 type="submit"
 disabled={uploading || !file}
 className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {uploading ? "Uploading…" : <>Upload <Upload size={14} /></>}
 </button>
 </div>
 </div>
 </form>

 {/* Gallery */}
 <div className="flex items-center justify-between mb-6">
 <div className="overline">Current Gallery · {photos.length} photo{photos.length === 1 ? "" : "s"}</div>
 </div>
 {loading ? (
 <div className="py-20 text-center overline" data-testid="admin-loading-photos">Loading…</div>
 ) : photos.length === 0 ? (
 <div className="py-20 text-center border" style={{ borderColor: "var(--hh-border)" }}>
 <ImageIcon size={32} strokeWidth={1} style={{ color: "var(--hh-gold)" }} className="mx-auto mb-3" />
 <div className="overline">No photos yet, upload your first above.</div>
 </div>
 ) : (
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-photo-grid">
 {photos.map((p) => (
 <div
 key={p.id}
 data-testid={`admin-photo-${p.id}`}
 className="relative group border"
 style={{ borderColor: "var(--hh-border)", background: "var(--hh-bg-2)" }}
 >
 <div className="aspect-[4/3] overflow-hidden">
 <img
 src={resolvePhotoUrl(p.url)}
 alt={p.caption || "Project photo"}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 <div className="p-3">
 <div className="text-[10px] tracking-[0.32em] uppercase mb-1" style={{ color: "var(--hh-gold)" }}>
 {p.source === "seeded" ? "Baseline" : "Uploaded"}
 </div>
 <div className="text-xs leading-relaxed truncate" style={{ color: "var(--hh-text-dim)" }}>
 {p.caption || ""}
 </div>
 </div>
 <button
 data-testid={`admin-delete-${p.id}`}
 onClick={() => setPendingDelete(p)}
 className="absolute top-2 right-2 w-9 h-9 inline-flex items-center justify-center border opacity-0 group-hover:opacity-100 transition-opacity"
 style={{
 background: "rgba(10,9,8,0.85)",
 borderColor: "var(--hh-gold)",
 color: "var(--hh-gold-light)",
 }}
 aria-label="Remove photo"
 >
 <Trash2 size={14} />
 </button>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* Delete confirm modal */}
 {pendingDelete && (
 <div
 data-testid="admin-delete-modal"
 className="fixed inset-0 z-[80] flex items-center justify-center p-6"
 style={{ background: "rgba(6,5,4,0.92)" }}
 onClick={() => setPendingDelete(null)}
 >
 <div
 onClick={(e) => e.stopPropagation()}
 className="w-full max-w-md border p-8"
 style={{ background: "var(--hh-bg-2)", borderColor: "var(--hh-gold)" }}
 >
 <AlertCircle size={28} strokeWidth={1} style={{ color: "var(--hh-gold)" }} className="mb-4" />
 <h2 className="font-display text-2xl mb-2">Remove this photo?</h2>
 <p className="text-sm mb-8" style={{ color: "var(--hh-text-dim)" }}>
 It will disappear from the public Projects page right away.
 {pendingDelete.source === "uploaded" && " The uploaded file will no longer be accessible."}
 </p>
 <div className="flex gap-3 justify-end">
 <button
 data-testid="admin-delete-cancel"
 onClick={() => setPendingDelete(null)}
 className="btn-ghost"
 >
 Cancel
 </button>
 <button
 data-testid="admin-delete-confirm"
 onClick={() => handleDelete(pendingDelete)}
 className="btn-gold"
 style={{ background: "#b34a3a", borderColor: "#b34a3a" }}
 >
 <Trash2 size={14} /> Remove
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
